// src/components/StructuredInstructions.tsx
import React from 'react';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export interface StructuredInstructionsType {
  goal: string;
  tone: string;
  persona: string;
  constraints: string;
  outputFormat: string;
}

interface StructuredInstructionsProps {
  instructions: StructuredInstructionsType;
  onChange: (field: keyof StructuredInstructionsType, value: string) => void;
}

export function StructuredInstructions({ instructions, onChange }: StructuredInstructionsProps) {
  return (
    <div className="grid gap-4 border p-4 rounded-lg">
      <h3 className="font-medium text-lg">Assistant Instructions</h3>
      <p className="text-sm text-gray-500">Craft clear instructions to define how your assistant should behave</p>
      
      <div className="grid gap-2">
        <Label htmlFor="goal">Goal</Label>
        <Textarea
          id="goal"
          value={instructions?.goal || ''}
          onChange={(e) => onChange('goal', e.target.value)}
          placeholder="What is the main purpose of this assistant? What problems does it solve?"
          rows={3}
          className="w-full resize-y font-serif"
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="tone">Tone</Label>
        <Textarea
          id="tone"
          value={instructions?.tone || ''}
          onChange={(e) => onChange('tone', e.target.value)}
          placeholder="How should the assistant communicate? (e.g., formal, friendly, professional)"
          rows={3}
          className="w-full resize-y font-serif"
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="persona">Persona</Label>
        <Textarea
          id="persona"
          value={instructions?.persona || ''}
          onChange={(e) => onChange('persona', e.target.value)}
          placeholder="Who is the assistant? What role does it play? Any specific expertise or background to simulate?"
          rows={3}
          className="w-full resize-y font-serif"
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="constraints">Constraints</Label>
        <Textarea
          id="constraints"
          value={instructions?.constraints || ''}
          onChange={(e) => onChange('constraints', e.target.value)}
          placeholder="What should the assistant avoid doing? Any limitations to be aware of?"
          rows={3}
          className="w-full resize-y font-serif"
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="outputFormat">Output Format</Label>
        <Textarea
          id="outputFormat"
          value={instructions?.outputFormat || ''}
          onChange={(e) => onChange('outputFormat', e.target.value)}
          placeholder="How should responses be structured? Any specific format to follow?"
          rows={3}
          className="w-full resize-y font-serif"
        />
      </div>
    </div>
  );
}

// Helper functions for structured instructions

export const combineInstructions = (structured: StructuredInstructionsType): string => {
  return `# Goal
${structured.goal || 'Not specified'}

# Tone
${structured.tone || 'Not specified'}

# Persona
${structured.persona || 'Not specified'}

# Constraints
${structured.constraints || 'Not specified'}

# Output Format
${structured.outputFormat || 'Not specified'}`;
};

export const parseInstructions = (instructions: string = ''): StructuredInstructionsType => {
  const defaultStructure = {
    goal: '',
    tone: '',
    persona: '',
    constraints: '',
    outputFormat: ''
  };

  if (!instructions) return defaultStructure;

  const sections = {
    goal: /# Goal\s*\n([\s\S]*?)(?=# Tone|# Persona|# Constraints|# Output Format|$)/i,
    tone: /# Tone\s*\n([\s\S]*?)(?=# Goal|# Persona|# Constraints|# Output Format|$)/i,
    persona: /# Persona\s*\n([\s\S]*?)(?=# Goal|# Tone|# Constraints|# Output Format|$)/i,
    constraints: /# Constraints\s*\n([\s\S]*?)(?=# Goal|# Tone|# Persona|# Output Format|$)/i,
    outputFormat: /# Output Format\s*\n([\s\S]*?)(?=# Goal|# Tone|# Persona|# Constraints|$)/i
  };

  const result = { ...defaultStructure };

  // Extract each section if it exists
  Object.entries(sections).forEach(([key, regex]) => {
    const match = instructions.match(regex);
    if (match && match[1]) {
      result[key as keyof StructuredInstructionsType] = match[1].trim();
    }
  });

  // If no structured format is found, put everything in the goal section
  if (!Object.values(result).some(value => value)) {
    result.goal = instructions.trim();
  }

  return result;
};